import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ForumPage = ({ user, isRealAdmin, role }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Form State
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine if viewer is admin
  const hasAdminAccess = role === 'ADMIN' || isRealAdmin;

  const fetchPosts = async () => {
    try {
      const res = await axios.get('/api/forum');
      setPosts(res.data);
    } catch (err) {
      console.error("Failed to load forum posts", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Use FormData to send both text and the PDF file
    const formData = new FormData();
    formData.append('title', title);
    formData.append('content', content);
    formData.append('authorName', 'Family Admin');
    if (file) {
      formData.append('pdfFile', file);
    }

    try {
      await axios.post('/api/forum', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setIsModalOpen(false);
      setTitle('');
      setContent('');
      setFile(null);
      fetchPosts(); // Refresh list
    } catch (err) {
      console.error("Failed to post", err);
      alert("Failed to create post. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if(!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await axios.delete(`/api/forum/${id}`);
      fetchPosts();
    } catch (err) {
      console.error("Failed to delete", err);
    }
  };

  // NEW: Securely fetch and open the PDF using Axios to bypass React Router
  const handleDownload = async (pdfUrl) => {
    try {
      // 1. Fetch the file data as a "blob" (raw binary data)
      const response = await axios.get(pdfUrl, { responseType: 'blob' });
      
      // 2. Create a temporary local URL for the file
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      
      // 3. Open the browser's native PDF viewer in a new tab
      window.open(fileURL, '_blank');
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Could not load the PDF. It may have been removed or the server is unreachable.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        
        {/* HEADER */}
        <div className="flex justify-between items-center border-b border-gray-200 pb-6 mb-8">
          <div>
            <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">Family Forum</h1>
            <p className="text-gray-500 mt-2 text-lg">Announcements, history, and important documents.</p>
          </div>
          
          {hasAdminAccess && (
            <button 
              onClick={() => setIsModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold shadow-md transition-all"
            >
              + Create Post
            </button>
          )}
        </div>

        {/* POSTS LIST */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-blue-600"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-gray-100 shadow-sm">
            <span className="text-5xl">📜</span>
            <h3 className="text-gray-500 font-semibold mt-4 text-lg">No posts yet.</h3>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map(post => (
              <div key={post.id} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm relative group">
                
                {hasAdminAccess && (
                  <button onClick={() => handleDelete(post.id)} className="absolute top-6 right-6 text-red-400 hover:text-red-600 font-bold text-sm">
                    Delete
                  </button>
                )}

                <h2 className="text-2xl font-bold text-gray-900 mb-2 pr-16">{post.title}</h2>
                <div className="text-xs text-gray-400 mb-4 font-semibold uppercase tracking-wide">
                  Posted by {post.authorName} on {new Date(post.createdAt).toLocaleDateString()}
                </div>
                
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{post.content}</p>
                
                {post.pdfUrl && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    {/* CHANGED: Swapped <a> tag for a <button> that triggers handleDownload */}
                    <button 
                      onClick={() => handleDownload(post.pdfUrl)}
                      className="inline-flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-5 py-2.5 rounded-lg font-bold transition-colors cursor-pointer"
                    >
                      <span className="text-lg">📄</span> View / Download PDF Attachment
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ADMIN UPLOAD MODAL */}
        {isModalOpen && hasAdminAccess && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden">
              <div className="px-6 py-4 bg-blue-600 flex justify-between items-center">
                <h3 className="text-white font-bold text-lg">Create New Announcement</h3>
                <button onClick={() => setIsModalOpen(false)} className="text-white hover:text-blue-200 text-2xl leading-none">&times;</button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Title</label>
                  <input required type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Message Content</label>
                  <textarea required rows="5" value={content} onChange={e => setContent(e.target.value)} className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none resize-y"></textarea>
                </div>
                
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 dashed-border">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Attach PDF Document (Optional)</label>
                  <input type="file" accept="application/pdf" onChange={e => setFile(e.target.files[0])} className="text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition">Cancel</button>
                  <button type="submit" disabled={isSubmitting} className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg transition disabled:opacity-50">
                    {isSubmitting ? 'Posting...' : 'Publish Post'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForumPage;









// import React, { useState, useEffect } from 'react';
// import axios from 'axios';

// const ForumPage = ({ user, isRealAdmin, role }) => {
//   const [posts, setPosts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [isModalOpen, setIsModalOpen] = useState(false);
  
//   // Form State
//   const [title, setTitle] = useState('');
//   const [content, setContent] = useState('');
//   const [file, setFile] = useState(null);
//   const [isSubmitting, setIsSubmitting] = useState(false);

//   // Determine if viewer is admin
//   const hasAdminAccess = role === 'ADMIN' || isRealAdmin;

//   const fetchPosts = async () => {
//     try {
//       const res = await axios.get('/api/forum');
//       setPosts(res.data);
//     } catch (err) {
//       console.error("Failed to load forum posts", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchPosts();
//   }, []);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
    
//     // Use FormData to send both text and the PDF file
//     const formData = new FormData();
//     formData.append('title', title);
//     formData.append('content', content);
//     formData.append('authorName', 'Family Admin');
//     if (file) {
//       formData.append('pdfFile', file);
//     }

//     try {
//       await axios.post('/api/forum', formData, {
//         headers: { 'Content-Type': 'multipart/form-data' }
//       });
//       setIsModalOpen(false);
//       setTitle('');
//       setContent('');
//       setFile(null);
//       fetchPosts(); // Refresh list
//     } catch (err) {
//       console.error("Failed to post", err);
//       alert("Failed to create post. Please try again.");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handleDelete = async (id) => {
//     if(!window.confirm("Are you sure you want to delete this post?")) return;
//     try {
//       await axios.delete(`/api/forum/${id}`);
//       fetchPosts();
//     } catch (err) {
//       console.error("Failed to delete", err);
//     }
//   };

//   return (
//     <div className="min-h-screen bg-gray-50 py-12 px-6">
//       <div className="max-w-4xl mx-auto">
        
//         {/* HEADER */}
//         <div className="flex justify-between items-center border-b border-gray-200 pb-6 mb-8">
//           <div>
//             <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">Family Forum</h1>
//             <p className="text-gray-500 mt-2 text-lg">Announcements, history, and important documents.</p>
//           </div>
          
//           {hasAdminAccess && (
//             <button 
//               onClick={() => setIsModalOpen(true)}
//               className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold shadow-md transition-all"
//             >
//               + Create Post
//             </button>
//           )}
//         </div>

//         {/* POSTS LIST */}
//         {loading ? (
//           <div className="flex justify-center py-20">
//             <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-blue-600"></div>
//           </div>
//         ) : posts.length === 0 ? (
//           <div className="text-center py-20 bg-white rounded-xl border border-gray-100 shadow-sm">
//             <span className="text-5xl">📜</span>
//             <h3 className="text-gray-500 font-semibold mt-4 text-lg">No posts yet.</h3>
//           </div>
//         ) : (
//           <div className="space-y-6">
//             {posts.map(post => (
//               <div key={post.id} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm relative group">
                
//                 {hasAdminAccess && (
//                   <button onClick={() => handleDelete(post.id)} className="absolute top-6 right-6 text-red-400 hover:text-red-600 font-bold text-sm">
//                     Delete
//                   </button>
//                 )}

//                 <h2 className="text-2xl font-bold text-gray-900 mb-2 pr-16">{post.title}</h2>
//                 <div className="text-xs text-gray-400 mb-4 font-semibold uppercase tracking-wide">
//                   Posted by {post.authorName} on {new Date(post.createdAt).toLocaleDateString()}
//                 </div>
                
//                 <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{post.content}</p>
                
//                 {post.pdfUrl && (
//                   <div className="mt-6 pt-6 border-t border-gray-100">
//                     <a 
//                       href={post.pdfUrl} 
//                       target="_blank" 
//                       rel="noopener noreferrer"
//                       className="inline-flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-5 py-2.5 rounded-lg font-bold transition-colors"
//                     >
//                       <span className="text-lg">📄</span> View / Download PDF Attachment
//                     </a>
//                   </div>
//                 )}
//               </div>
//             ))}
//           </div>
//         )}

//         {/* ADMIN UPLOAD MODAL */}
//         {isModalOpen && hasAdminAccess && (
//           <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
//             <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden">
//               <div className="px-6 py-4 bg-blue-600 flex justify-between items-center">
//                 <h3 className="text-white font-bold text-lg">Create New Announcement</h3>
//                 <button onClick={() => setIsModalOpen(false)} className="text-white hover:text-blue-200 text-2xl leading-none">&times;</button>
//               </div>
              
//               <form onSubmit={handleSubmit} className="p-6 space-y-5">
//                 <div>
//                   <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Title</label>
//                   <input required type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none" />
//                 </div>
                
//                 <div>
//                   <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Message Content</label>
//                   <textarea required rows="5" value={content} onChange={e => setContent(e.target.value)} className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none resize-y"></textarea>
//                 </div>
                
//                 <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 dashed-border">
//                   <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Attach PDF Document (Optional)</label>
//                   <input type="file" accept="application/pdf" onChange={e => setFile(e.target.files[0])} className="text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
//                 </div>

//                 <div className="flex gap-3 pt-4">
//                   <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition">Cancel</button>
//                   <button type="submit" disabled={isSubmitting} className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg transition disabled:opacity-50">
//                     {isSubmitting ? 'Posting...' : 'Publish Post'}
//                   </button>
//                 </div>
//               </form>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ForumPage;